
import React from 'react';
import {
  DataGrid,
  GridColDef,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import { Box, Toolbar, Typography } from '@mui/material';
import { deDE } from '@mui/x-data-grid/locales';
import { useDataGridStyles } from '@/styles/dataGridStyles';
import {useIsMobile} from "@/hooks/use-mobile.tsx";

interface AppDataGridProps {
  rows: any[];
  columns: GridColDef[];
  loading?: boolean;
  autoHeight?: boolean;
  density?: 'compact' | 'standard' | 'comfortable';
  disableRowSelectionOnClick?: boolean;
  getRowId?: (row: any) => string;
  initialState?: any;
  slots?: {
    toolbar?: React.ComponentType<any>;
    [key: string]: any;
  };
  slotProps?: {
    toolbar?: any;
    [key: string]: any;
  };
  onRowClick?: (params: any) => void;
  sx?: any;
  title?: string;
}

const AppDataGrid: React.FC<AppDataGridProps> = ({
  rows,
  columns,
  loading = false,
  autoHeight = true,
  disableRowSelectionOnClick = true,
  getRowId,
  initialState,
  slots,
  slotProps,
  onRowClick,
  sx = {},
  title,
}) => {
  const styles = useDataGridStyles();
  const isMobile = useIsMobile();

  const defaultInitialState = {
    pagination: {
      paginationModel: { pageSize: 50, page: 0 },
    },
    ...initialState
  };

  // Custom toolbar that shows title and quick search
  const CustomToolbar = () => {
    return (
      <Toolbar sx={{
        p: 0,
        display: 'flex',
        justifyContent: 'space-between',
        minHeight: '48px', // Reduce the default height
        '&.MuiToolbar-root': {
          paddingLeft: 0,
          paddingRight: 0
        }
      }}>
        <Typography variant="h6" component="div" sx={{ fontSize: '1rem', ml: isMobile ? 0 : 1}}>
          {title}
        </Typography>
        <GridToolbarQuickFilter sx={{width: '170px'}}/>
      </Toolbar>
    );
  };

  const defaultSlots = {
    toolbar: CustomToolbar,
    ...slots
  };

  const defaultSlotProps = {
    toolbar: {
      ...slotProps?.toolbar
    },
    ...slotProps
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        height: autoHeight ? 'auto' : 500,
        width: '100%',
        overflow: 'hidden',
        '& .MuiDataGrid-root': styles.root,
        '& .MuiDataGrid-toolbarContainer': {
          padding: 0,
        },
        ...styles.layout,
        ...styles.cells,
        ...styles.headers,
        ...styles.focus,
        ...sx
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        density={"compact"}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        getRowId={getRowId}
        initialState={defaultInitialState}
        slots={defaultSlots}
        slotProps={defaultSlotProps}
        onRowClick={onRowClick}
        localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
        pageSizeOptions={[5, 10, 25, 50, 100]}
      />
    </Box>
  );
};

export default AppDataGrid;
